import {DipRegistrationContext} from '../../models/dip-registration-context.model';
import {SecurityContext} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';

export const DIPRS_CONTEXT_TOKEN = 'DIPRS_CONTEXT';

// Allowed domains for script loading (whitelist)
const ALLOWED_SCRIPT_DOMAINS = [
  'nexus.ensighten.com',
  'cdn.cookielaw.org',
  'nexus.ensighten.com',
  'bcdn.td.com',
  'bcdn-test.td.com',
  'assets.adobedtm.com'
];

export function joinObjectKeyValues(obj, sign) {
  return obj ? Object.keys(obj)
    .filter(key => obj[key] !== undefined && obj[key] !== '' && obj[key] !== null)
    .map(key => key + '=' + obj[key]).join(sign) : '';
}

export interface IScriptAttributes {
  name: string;
  value: string;
}

/**
 * Validates and sanitizes the scriptUrl to prevent CWE-23 (Path Traversal) attacks.
 *
 * Security checks performed:
 * 1. URL must use HTTPS or HTTP protocol only
 * 2. URL must not contain path traversal sequences (../, ..\)
 * 3. URL domain must be in the allowed whitelist
 * 4. URL must be a valid URL format
 *
 * @param scriptUrl - The URL to validate
 * @returns true if the URL is valid and safe, false otherwise
 * @throws Error if the URL is malicious
 */
export function validateScriptUrl(scriptUrl: string): boolean {
  if (!scriptUrl || typeof scriptUrl !== 'string') {
    return false;
  }

  // Check for path traversal sequences
  if (scriptUrl.includes('../') || scriptUrl.includes('..\\') || scriptUrl.includes('..') && scriptUrl.includes('/')) {
    console.error('Path traversal detected in scriptUrl');
    return false;
  }

  try {
    const url = new URL(scriptUrl);

    // Only allow HTTPS and HTTP protocols
    if (!['https:', 'http:'].includes(url.protocol)) {
      console.error('Invalid protocol in scriptUrl');
      return false;
    }

    // Validate the hostname is in the allowed domains
    const hostname = url.hostname.toLowerCase();
    const isAllowed = ALLOWED_SCRIPT_DOMAINS.some(domain =>
      hostname === domain.toLowerCase() ||
      hostname.endsWith('.' + domain.toLowerCase())
    );

    if (!isAllowed) {
      console.error('Domain not whitelisted for scriptUrl');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Invalid URL format in scriptUrl', scriptUrl);
    return false;
  }
}

/**
 * Two-phase URL safety gate used before any third-party script is loaded.
 *
 * Phase 1 – CWE-79 (XSS): delegates to Angular DomSanitizer.sanitize with
 *   SecurityContext.URL.  The sanitizer strips dangerous protocols (javascript:,
 *   data:, vbscript:, …) and returns null for inputs it considers unsafe.
 *
 * Phase 2 – CWE-23 (Path Traversal) + domain allow-list: delegates to
 *   validateScriptUrl which checks for ../ sequences, enforces HTTPS/HTTP and
 *   matches the hostname against ALLOWED_SCRIPT_DOMAINS.
 *
 * Accepting the DomSanitizer as a parameter keeps this function framework-
 * agnostic and fully unit-testable without an Angular TestBed.
 *
 * @param sanitizer  Angular DomSanitizer instance (injected by the caller)
 * @param url        Raw URL string received from the backend context object
 * @returns true only when the URL passes both phases
 */
export function sanitizeAndValidateUrl(sanitizer: DomSanitizer, url: string): boolean {
  if (!url) {
    return false;
  }
  // Phase 1 – DomSanitizer enforces CWE-79.
  // sanitize() returns null when the URL is dangerous.
  const sanitized = sanitizer.sanitize(SecurityContext.URL, url);
  if (!sanitized) {
    console.error('Script URL rejected by DomSanitizer', url);
    return false;
  }
  // Phase 2 – validateScriptUrl enforces CWE-23 + domain whitelist.
  return validateScriptUrl(sanitized);
}

export function loadScript(scriptUrl: string, attributes: IScriptAttributes[] = []): Promise<any> {
  if (scriptUrl) {
    return new Promise((resolve) => {
      try {
        // Validate and sanitize the scriptUrl
        if (!validateScriptUrl(scriptUrl)) {
          resolve({loaded: false, status: 'Validation Failed', error: 'Script URL validation failed'});
          return;
        }

        // load script
        const script: any = document.createElement('script');
        script.type = 'text/javascript';
        script.src = scriptUrl;

        if (attributes.length > 0) {
          for (const attribute of attributes) {
            script.setAttribute(attribute.name, attribute.value);
          }
        }

        if (script.readyState) {  // IE
          script.onreadystatechange = () => {
            if (script.readyState === 'loaded' || script.readyState === 'complete') {
              script.onreadystatechange = null;
              resolve({loaded: true, status: 'Loaded'});
            }
          };
        } else {  // Others
          script.onload = () => {
            resolve({loaded: true, status: 'Loaded'});
          };
        }
        script.onerror = () => resolve({loaded: false, status: 'Loaded'});
        document.head.appendChild(script);
      } catch (error) {
        // Validation failed, resolve with error status
        console.error('Error loading script:', error);
        resolve({loaded: false, status: 'Validation Failed', error: error instanceof Error ? error.message : String(error)});
      }
    });
  } else {
    return Promise.resolve();
  }
}

export function getUrlParameter(parameter): string {
  return (new URL(location.href)).searchParams.get(parameter);
}

export function getApiUrl(): string {
  return `${window.location.protocol}//${window.location.host}/customer-registration`;
}

export function getDiprsContext(): DipRegistrationContext {
  const staticContext = window[DIPRS_CONTEXT_TOKEN] as DipRegistrationContext;
  return staticContext || {} as DipRegistrationContext;
}
